import React, { useMemo, useState } from 'react'
import {
  Box,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  SvgIcon,
  useMediaQuery,
} from '@mui/material'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import Button from '@hapana/shared/web/components/shared/button/button.component.jsx'
import downloadIcon from '@hapana/shared/web/styles/assets/svgs/download-small.svg'
import searchIcon from '@hapana/shared/web/styles/assets/svgs/search-small.svg'
import {
  useActiveExportRequests,
  useClearExportRequestHistory,
  useDeleteExportRequest,
  useExportReports,
} from '_pages/exports/exports.hooks'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import { BUTTON_VARIANTS } from '@hapana/shared/web/components/shared/button/button.constants'
import { getActiveExportRequests } from '_services/exports.service'

import ExportNotification from '../export-notification/export-notification.component.jsx'

import ExportRequestList from './export-request-list.component.jsx'
import { styles } from './export-widgets.styles'
import ExportRequestMobile from './export-request-mobile.component.jsx'
import { EXPORT_STATUS } from './export-widgets.constants'
import { useSupportAssistant } from '../support-assistant/support-assistant.hooks'

const ExportWidgets = ({ hasDownloadPermission = true }) => {
  const [idsLoading, setIdsLoading] = useState([])
  const [open, setOpen] = useState(false)
  const { setIsOpen } = useSupportAssistant()

  useExportReports()

  const {
    data: activeExportRequests,
    isLoading,
    isRefetching,
  } = useActiveExportRequests({
    onSuccess: (data) => {
      if (data && Array.isArray(data)) {
        setDialogState({
          requestHistory: data
            .filter(({ status }) => status === EXPORT_STATUS.processed)
            .reduce((acc, curr) => {
              acc[curr.id] = curr
              return acc
            }, {}),
        })
      }
    },
  })
  const clearExportRequestHistory = useClearExportRequestHistory()
  const deleteExportRequest = useDeleteExportRequest()

  const { dialogState, setDialogState } = useDialog()
  const queryClient = useQueryClient()

  const activeRequests = useMemo(
    () =>
      (activeExportRequests &&
        activeExportRequests.filter(({ status }) => status === EXPORT_STATUS.started)) ||
      [],
    [activeExportRequests]
  )

  const fullRequestHistory = useMemo(
    () =>
      Object.values(dialogState.requestHistory || {}).sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
      ),
    [dialogState.requestHistory]
  )

  const onRemoveRequest = (requestId) => {
    setIdsLoading(() => [requestId])
    setDialogState({
      idsLoading: [requestId],
    })

    deleteExportRequest.mutate(requestId, {
      onError: () => {
        setIdsLoading(() => [])
      },
      onSettled: () => {
        setIdsLoading(() => [])
      },
      onSuccess: async () => {
        setIdsLoading(() => [])
      },
    })
  }

  const onClearRequestHistory = () => {
    clearExportRequestHistory.mutate()
    setDialogState({ requestHistory: {} })
    setDialogState({ hasRequestDownloadNotification: false })
  }

  const { t } = useTranslation(['common', 'core'])

  const [anchorEl, setAnchorEl] = useState(null)
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const { pushDialog } = useDialog()

  const showDownloads = async (event) => {
    setAnchorEl(event.target.current)
    setOpen((prev) => !prev)

    queryClient.invalidateQueries({ queryKey: getActiveExportRequests.key })
  }

  const showMobileDownloads = async () => {
    queryClient.invalidateQueries({ queryKey: getActiveExportRequests.key })

    pushDialog(ExportRequestMobile, {
      onRemoveRequest,
      onClearRequestHistory,
    })
  }

  const handleClose = () => {
    setOpen(false)
  }

  return isMobile ? (
    <ListItemButton sx={styles.itemButton} onClick={showMobileDownloads}>
      <ListItemIcon sx={styles.itemIcon}>
        <SvgIcon component={downloadIcon} />
      </ListItemIcon>
      <ListItemText sx={styles.bottomItemLabel} primary={t('core:exports.viewExportRequests')} />
    </ListItemButton>
  ) : (
    <>
      <Box sx={styles.actionsRow}>
        {hasDownloadPermission && (
          <Button
            sx={styles.downloadButton(open)}
            variant={BUTTON_VARIANTS.TEXT}
            endIcon={downloadIcon}
            onClick={showDownloads}
            label={
              activeRequests.length > 0 && <Box sx={styles.countBox}>{activeRequests.length}</Box>
            }
          />
        )}

        <Button
          sx={styles.supportButton}
          variant={BUTTON_VARIANTS.TEXT}
          endIcon={searchIcon}
          onClick={() => setIsOpen(true)}
          aria-label={t('common:navigationLinks.helpCenter', 'Open support assistant')}
          data-testid="export-widgets-help-center"
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: styles.menuPaper }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <ExportRequestList
          activeRequests={activeRequests}
          requestHistory={fullRequestHistory}
          idsLoading={idsLoading}
          isLoading={isLoading || isRefetching}
          onRemoveRequest={onRemoveRequest}
          onClearRequestHistory={onClearRequestHistory}
          onClose={handleClose}
        />
      </Menu>

      {dialogState.latestExportRequest && dialogState.latestExportRequest.id ? (
        <ExportNotification key={dialogState.latestExportRequest.id} />
      ) : null}
    </>
  )
}

ExportWidgets.propTypes = {
  activeRequests: PropTypes.array,
  requestHistory: PropTypes.array,
  idsLoading: PropTypes.array,
  onRemoveRequest: PropTypes.func,
  onClearRequestHistory: PropTypes.func,
  isDisabled: PropTypes.bool,
  hasDownloadPermission: PropTypes.bool,
}

ExportWidgets.defaultValues = {
  idsLoading: [],
  activeRequests: [],
  requestHistory: [],
}

export default ExportWidgets
